import config from '../../config';

export default class PremiumDiscoveryService {
  FEATURE_NAMES = {
    TASKS: 'Tasks',
    LABOR_COST: 'Labor cost',
    TIME_CLOCK: 'Time clock',
    REPORTS: 'Reports',
    PTO: 'PTO',
    TAGS: 'Tags',
  };

  _triggerHandlers = new Set();

  constructor(Authentication, Utils, $timeout) {
    'ngInject';

    Object.assign(this, { Authentication, Utils, $timeout });

    this.features = this._getFeatures();
  }

  init() {
    const check = async () => {
      if (!this.premiumDiscovery._halted && this.canDisplayPremiumDiscovery()) {
        if (
          !this.premiumDiscovery._lastAutoShow ||
          this.Utils.getNowDate() - new Date(this.premiumDiscovery._lastAutoShow) >
            config.PREMIUM_DISCOVERY_STEPS_DELAY
        ) {
          await this.$timeout(config.PREMIUM_DISCOVERY_BACKOFF);

          if (this.Utils.setToArray(this._triggerHandlers).some((h) => h())) {
            this.premiumDiscovery._lastAutoShow = this.Utils.getNowDate().toISOString();
            this.Authentication.syncSettings();
          }
        }
      }

      await this.$timeout(config.PREMIUM_DISCOVERY_CHECK_INTERVAL);
      check();
    };

    check();
  }

  canDisplayPremiumDiscovery() {
    const now = this.Utils.getNowDate();
    const ONE_DAY_MS = 60 * 60 * 24 * 1000;
    const { createdAt } = this.Authentication.user.org;
    const created = new Date(createdAt).getTime();

    return now.getTime() - ONE_DAY_MS > created;
  }

  _getFeatures() {
    return [
      {
        name: this.FEATURE_NAMES.TASKS,
        state: 'shiftTasks.instances',
        description:
          "Distribute tasks across your team and make it clear what needs to be done during each shift. Allow employees to easily access their to-dos right from their phones. Improve clarity, efficiency and your employees' performance",
      },
      {
        name: this.FEATURE_NAMES.LABOR_COST,
        state: 'labor-cost',
        description:
          'Optimize your spending as you schedule. Specify wages, and monitor how much each shift is costing you, and keep an eye on your labor spent per period.',
      },
      {
        name: this.FEATURE_NAMES.TIME_CLOCK,
        state: 'timeclock',
        description:
          'Simplify your time-tracking process with the time clock feature on Sling. Easily track employee hours and export timesheets for seamless payroll processing.',
      },
      {
        name: this.FEATURE_NAMES.REPORTS,
        state: 'reports',
        description:
          'Analyze and understand your labor data by pulling reports for a day, week or month, or for customizable periods. Make adjustments for the future and drive your business forward.',
      },
      {
        name: this.FEATURE_NAMES.PTO,
        state: 'settings',
        stateParams: { tab: 'timeOffs' },
        description:
          'Set PTO caps per leave type or in aggregate and allow employees to request paid time off for leave types your business covers. ',
      },
      {
        name: this.FEATURE_NAMES.TAGS,
        state: 'dashboard.manage.tags.list',
        description:
          'Create tags to add further details to shifts to ensure all jobs are covered, and employees are aware of what’s expected of them during the shift.',
      },
    ];
  }

  onTrigger(fn) {
    this._triggerHandlers.add(fn);
    return () => this._triggerHandlers.delete(fn);
  }

  get premiumDiscovery() {
    return this.Authentication.user.settings.clientFlags.premiumDiscovery;
  }

  get currentFeature() {
    return this._currentFeature || this.features.find((f) => !this.premiumDiscovery[f.name]);
  }

  setCurrentFeature(feature) {
    this._currentFeature = feature;
  }

  featureDiscovered(name, userSettings = null) {
    const dict = userSettings ? userSettings.clientFlags.premiumDiscovery : this.premiumDiscovery;
    if (!dict._relevant) {
      return;
    }

    dict[name] = true;

    if (Object.values(this.FEATURE_NAMES).every((n) => dict[n])) {
      dict._done = true;
    }

    if (!userSettings) {
      this.Authentication.syncSettings();
    }

    delete this._currentFeature;
  }

  isPopupVisible() {
    const { user } = this.Authentication;
    return (
      user &&
      user.isManager && // show only to a manager or admin
      !user.org.subscriptionStartDate && // show only if premium hasn't been purchased
      !user.org.subscriptionCancelDate && // show only if premium hasn't been purchased and then cancelled
      user.org.isPremium && // show only if trial is still ongoing
      this.premiumDiscovery._relevant &&
      !this.premiumDiscovery._done
    );
  }
}
